import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CustomDatePicker = ({ value, onChange, minDate, maxDate, placeholder }) => {
  const { t } = useTranslation('dashboard');
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);

  // Get month names from translations (English fallbacks inline — the Super
  // Admin app never edits locale JSONs from feature work)
  const monthNames = [
    t('dashboard.months.january', 'January'),
    t('dashboard.months.february', 'February'),
    t('dashboard.months.march', 'March'),
    t('dashboard.months.april', 'April'),
    t('dashboard.months.may', 'May'),
    t('dashboard.months.june', 'June'),
    t('dashboard.months.july', 'July'),
    t('dashboard.months.august', 'August'),
    t('dashboard.months.september', 'September'),
    t('dashboard.months.october', 'October'),
    t('dashboard.months.november', 'November'),
    t('dashboard.months.december', 'December'),
  ];

  // Initialize currentMonth when value changes
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setCurrentMonth(date);
      }
    }
  }, [value]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get days in month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get first day of month (0 = Monday, 1 = Tuesday, ..., 6 = Sunday)
  const getFirstDayOfMonth = (date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return (day + 6) % 7; // Convert from Sunday=0 to Monday=0
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse date string YYYY-MM-DD to Date object
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Format display date
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = parseDate(dateString);
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day} - ${month} - ${year}`;
  };

  // Handle previous month
  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Handle next month
  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    
    // Don't allow navigating to months beyond maxDate
    if (maxDate) {
      const max = parseDate(maxDate);
      if (max && nextMonth > max) {
        return; // Don't navigate to future months
      }
    }
    
    setCurrentMonth(nextMonth);
  };

  // Handle date selection
  const handleDateClick = (day) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    // Check if date is before minDate
    if (minDate) {
      const min = parseDate(minDate);
      if (min && selectedDate < min) {
        return; // Don't select dates before minDate
      }
    }
    
    // Check if date is after maxDate
    if (maxDate) {
      const max = parseDate(maxDate);
      if (max && selectedDate > max) {
        return; // Don't select dates after maxDate
      }
    }
    
    onChange({ target: { value: formatDate(selectedDate) } });
    setIsOpen(false);
  };

  // Check if date is selected
  const isSelected = (day) => {
    if (!value) return false;
    const selectedDate = parseDate(value);
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Check if date is today
  const isToday = (day) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Check if date is disabled (before minDate or after maxDate)
  const isDisabled = (day) => {
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    // Check if date is before minDate
    if (minDate) {
      const min = parseDate(minDate);
      if (min && checkDate < min) {
        return true;
      }
    }
    
    // Check if date is after maxDate
    if (maxDate) {
      const max = parseDate(maxDate);
      if (max && checkDate > max) {
        return true;
      }
    }
    
    return false;
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add all days in the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  return (
    <div className="relative" ref={datePickerRef} data-testid="custom-date-picker-container">
      <input
        type="text"
        value={formatDisplayDate(value)}
        placeholder={placeholder}
        readOnly
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 border rounded-xl bg-white text-gray-700 border-gray-200 hover:border-[#4ABDBD] cursor-pointer focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none transition text-sm font-medium"
        data-testid="custom-date-picker-input"
      />
      <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" data-testid="custom-date-picker-icon" />

      {isOpen && (
        <div className="absolute top-full left-0 right-auto mt-2 bg-white rounded-xl shadow-xl border border-gray-200/50 z-50 overflow-hidden custom-calendar-picker max-w-[calc(100vw-2rem)]" data-testid="custom-date-picker-dropdown">
          {/* Calendar Header */}
          <div className="bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] px-4 py-3 flex items-center justify-between" data-testid="custom-date-picker-header">
            <button
              type="button"
              onClick={handlePreviousMonth}
              className="p-1 hover:bg-white/20 rounded-md transition-colors cursor-pointer"
              data-testid="custom-date-picker-prev-button"
            >
              <ChevronLeft className="w-5 h-5 text-white" data-testid="custom-date-picker-prev-icon" />
            </button>
            <div className="text-white font-semibold text-sm" data-testid="custom-date-picker-month-label">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 hover:bg-white/20 rounded-md transition-colors cursor-pointer"
              data-testid="custom-date-picker-next-button"
            >
              <ChevronRight className="w-5 h-5 text-white" data-testid="custom-date-picker-next-icon" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="p-4" data-testid="custom-date-picker-grid">
            {/* Day names */}
            <div className="grid grid-cols-7 gap-1 mb-2" data-testid="custom-date-picker-day-names">
              {[
                t('dashboard.days_short.mon', 'Mo'),
                t('dashboard.days_short.tue', 'Tu'),
                t('dashboard.days_short.wed', 'We'),
                t('dashboard.days_short.thu', 'Th'),
                t('dashboard.days_short.fri', 'Fr'),
                t('dashboard.days_short.sat', 'Sa'),
                t('dashboard.days_short.sun', 'Su')
              ].map((day, index) => (
                <div
                  key={index}
                  className="text-center text-xs font-semibold text-gray-500 py-1"
                  data-testid={`custom-date-picker-day-name-${index}`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1" data-testid="custom-date-picker-days">
              {calendarDays.map((day, index) => (
                <div key={index}>
                  {day ? (
                    <button
                      type="button"
                      onClick={() => !isDisabled(day) && handleDateClick(day)}
                      disabled={isDisabled(day)}
                      className={`
                        w-8 h-8 text-sm rounded-lg transition-all duration-200 flex items-center justify-center
                        ${isSelected(day)
                          ? 'bg-[#4ABDBD] text-white font-semibold shadow-md'
                          : isToday(day)
                          ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold'
                          : isDisabled(day)
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-[#4ABDBD]/20 hover:text-[#4ABDBD]'
                        }
                      `}
                      data-testid={`custom-date-picker-day-${day}`}
                    >
                      {day}
                    </button>
                  ) : (
                    <div className="w-8 h-8"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;

